<template>
  <v-dialog v-model="value" persistent scrollable max-width="80vw">
    <v-overlay :value="updating">
      <div class="text-center">
        <v-progress-linear
          color="light-blue"
          height="10"
          :value="progress"
          striped
        ></v-progress-linear>
        <h4 class="mt-4">Uploading image, please wait....</h4>
      </div>
    </v-overlay>
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>New Artwork</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-2">
        <v-form ref="formbase" v-model="formValid" lazy-validation @submit.prevent>
          <v-row class="py-4">
            <v-col cols="12" class="py-0">
              <v-text-field
                v-model="cardTitle"
                outlined
                clearable
                clear-icon="mdi-close-box"
                dense
                :rules="[rules.required]"
                label="Title"
                hide-details:
                @blur="isDirty = true"
              >
              </v-text-field>
            </v-col>
            <v-col class="py-0">
              <v-file-input
                label="Image(s) to upload"
                v-model="selectedImage"
                prepend-icon="mdi-image"
                accept="image/*"
                multiple
                @change="handleImageUpdate"
              ></v-file-input>
            </v-col>
          </v-row>
        </v-form>
        <v-expansion-panels v-if="images.length" accordion v-model="panel">
          <v-expansion-panel v-for="image of images" :key="image.id">
            <v-expansion-panel-header>
              <v-img max-height="50px" max-width="50px" :src="image.url"></v-img>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" class="pt-4">
                  <v-autocomplete
                    v-model="selectedImageProducts[image.id]"
                    :items="productTypes"
                    outlined
                    clearable
                    clear-icon="mdi-close-box"
                    dense
                    multiple
                    chips
                    hide-details:
                    item-text="title"
                    label="Products"
                    :return-object="true"
                    @change="isDirty = true"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        class="ma-2"
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click:close="removeProduct(data.item)"
                      >
                        {{ data.item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" class="pt-4">
                  <v-card
                    v-for="product of selectedImageProducts[image.id]"
                    :key="product.id"
                  >
                    <v-card-title>Select {{ product.title }} products </v-card-title>
                    <v-card-text>
                      <v-autocomplete
                        v-model="product.variants"
                        :items="getProductVariants(product.productTypeCode)"
                        outlined
                        clearable
                        clear-icon="mdi-close-box"
                        dense
                        multiple
                        chips
                        hide-details:
                        item-text="description"
                        label="Variants"
                        :return-object="true"
                        @change="isDirty = true"
                      >
                        <template v-slot:selection="data">
                          <v-chip
                            class="ma-2"
                            v-bind="data.attrs"
                            :input-value="data.selected"
                            close
                            @click:close="removeVariant(product, data.item)"
                          >
                            {{ data.item.description }}
                          </v-chip>
                        </template>
                      </v-autocomplete>
                      <v-row>
                        <v-col v-for="variant of product.variants" :key="variant.id">
                          <cropper
                            style="width: 50%"
                            :src="image.url"
                            :stencil-component="$options.components.CircleStencil"
                          />
                          <h3>text</h3>
                          <pre>{{ variant }}</pre>
                        </v-col></v-row
                      >
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions>
        <v-btn
          :disabled="!isDirty || !formValid"
          color="success"
          class="mr-4"
          @click.native="saveArtwork()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import "vue-advanced-cropper/dist/style.css";
import { mapGetters } from "vuex";
import axios from "axios";
import S3Api from "@/api/s3";
import { CircleStencil, Cropper } from "vue-advanced-cropper";
import {
  resizeImage,
  readFileAsDataURL,
  dataURLtoFile,
  fetchBlob,
} from "@/utilities/imageUtilities";

const server = process.env.VUE_APP_API_URL || "";
const api = `${server}/api/artwork`;

export default {
  props: {
    value: Boolean,
  },
  components: {
    Cropper,
    CircleStencil,
  },
  async mounted() {},
  data() {
    return {
      panel: null,
      updating: false,
      currentImage: null,
      progress: 0,
      isDirty: false,
      images: [],
      selectedImage: null,
      cardTitle: null,
      formValid: true,
      filesArray: [],
      selectedImageProducts: {},
      selectedProducts: [],
      products: [],
      productTypes: [],
      rules: {
        imageRequired: () => {
          // if (this.artwork.image) return false
          return !this.artwork.image || "An artwork image is required";
        },
        required: (value) => !!value || "Required.",
      },
    };
  },
  computed: {
    ...mapGetters(["user"]),
    ...mapGetters({ productTypeVariants: "products/productTypeVariants" }),
  },
  async mounted() {},
  async created() {
    this.productTypes = JSON.parse(
      JSON.stringify(this.$store.getters["products/productTypes"])
    );
  },
  methods: {
    getProductVariants(productTypeCode) {
      return this.productTypeVariants.filter(
        (item) => item.productTypeCode === productTypeCode
      );
    },
    removeProduct(item) {
      const index = this.selectedProducts.findIndex(
        (v) => v.productTypeCode === item.productTypeCode
      );
      if (index >= 0) this.selectedProducts.splice(index, 1);
    },
    removeVariant(product, item) {
      const index = product.variants.findIndex(
        (v) => v.productTypeVariantCode === item.productTypeVariantCode
      );
      if (index >= 0) product.variants.splice(index, 1);
    },
    async handleImageUpdate() {
      this.images = [];
      if (this.selectedImage) {
        for (let i = 0; i < this.selectedImage.length; i++) {
          this.filesArray.push({ id: `image-${i}`, file: this.selectedImage[i] });
          const b64str = await readFileAsDataURL(this.selectedImage[i]);
          const _IMG300 = await resizeImage(b64str, { maxHeight: 1000, maxWidth: 1000 });
          this.images.push({ id: `image_${i}`, url: _IMG300.url });
        }
      } else {
      }
    },
    async saveArtwork() {
      let uploads = [];
      let totalSize = 0;
      let totalLoaded = 0;
      let imageProgress = [];
      this.updating = true;
      for (const index in this.filesArray) {
        totalSize += this.filesArray[index].file.size;
        imageProgress[index] = 0;
      }
      for (const index in this.filesArray) {
        const image = this.filesArray[index];
        const data = {
          path: `artwork-uploads/${this.user.artist.artistCode}`,
          name: image.file.name,
          file: image.file,
        };
        const postData = await S3Api.getS3PresignedPost(
          `${data.path}/${data.name}`,
          "S3_CUSTOMER_UPLOADS_BUCKET"
        );
        const formData = new FormData();
        formData.append("file", data.file, data.name);
        uploads.push(
          axios.put(postData.data, data.file, {
            onUploadProgress: (progressEvent) => {
              imageProgress[index] = progressEvent.loaded;
              totalLoaded = imageProgress.reduce((a, b) => a + b, 0);
              this.progress = (totalLoaded / totalSize) * 100;
              this.currentImage = data.name;
            },
            headers: {
              "Content-Type": "image/jpeg",
            },
          })
        );
      }
      Promise.all(uploads).then(async (result) => {
        // const url = await S3Api.getS3PresignedGet(`artwork-uploads/${this.user.artist.artistCode}/mytest.jpg`, "S3_CUSTOMER_UPLOADS_BUCKET")
        this.updating = false;
        this.$emit("close");
      });
    },
    close() {
      this.$emit("close");
    },
    log(v) {
      // this.variant.productTypeVariantCode && this.variant.productTypeVariantCode.toUpperCase()
      this.isDirty = true;
      this.formValid = this.$refs.formbase.validate();
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep.v-dialog__content {
  align-items: start;
  margin-top: 50px;
}
</style>
